import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { Tooltip, Pill } from '@cardstack/boxel-ui/components';
import { and, gt, not } from '@cardstack/boxel-ui/helpers';

import type { CardErrorJSONAPI } from '@cardstack/runtime-common';
import {
  localId,
  isCardInstance,
  isCardErrorJSONAPI,
} from '@cardstack/runtime-common';

import {
  requiredModality,
  modalityLabel,
  isTextBasedContentType,
} from '@cardstack/runtime-common/ai/modality';

import CardPill from '@cardstack/host/components/card-pill';
import FilePill from '@cardstack/host/components/file-pill';
import type {
  FileUploadState,
  FileUploadStatus,
} from '@cardstack/host/lib/file-upload-state';
import { urlForRealmLookup } from '@cardstack/host/lib/utils';

import type OperatorModeStateService from '@cardstack/host/services/operator-mode-state-service';

import type { CardDef } from 'https://cardstack.com/base/card-api';
import type { FileDef } from 'https://cardstack.com/base/file-api';

import type { TrackedSet } from 'tracked-built-ins';

const MAX_ITEMS_TO_DISPLAY = 4;

interface Signature {
  Element: HTMLDivElement;
  Args: {
    items: (CardDef | FileDef | CardErrorJSONAPI)[];
    autoAttachedCardIds?: TrackedSet<string>;
    autoAttachedFiles?: FileDef[];
    removeCard: (cardId: string) => void;
    removeFile: (file: FileDef) => void;
    chooseCard?: (cardId: string) => void;
    chooseFile?: (file: FileDef) => void;
    isLoaded: boolean;
    autoAttachedCardTooltipMessage?: string;
    fileUploadStates?: ReadonlyMap<string, FileUploadState>;
    retryFileUpload?: (file: FileDef) => void;
    inputModalities?: string[];
  };
}

export default class AttachedItems extends Component<Signature> {
  @tracked areAllItemsDisplayed = false;

  @service declare private operatorModeStateService: OperatorModeStateService;

  get itemsToDisplay() {
    return this.areAllItemsDisplayed
      ? this.args.items
      : this.args.items.slice(0, MAX_ITEMS_TO_DISPLAY);
  }

  private isCard = (item: CardDef | FileDef): item is CardDef => {
    return isCardInstance(item);
  };

  private isAutoAttachedCard = (cardId: string): boolean => {
    return this.args.autoAttachedCardIds?.has(cardId) ?? false;
  };

  private isAutoAttachedFile = (file: FileDef): boolean => {
    return (
      this.args.autoAttachedFiles?.some(
        (autoFile) => autoFile.sourceUrl === file.sourceUrl,
      ) ?? false
    );
  };

  @action
  private toggleViewAllAttachedCards() {
    this.areAllItemsDisplayed = !this.areAllItemsDisplayed;
  }

  private getCardErrorRealm(cardError: CardErrorJSONAPI) {
    return cardError.realm ?? this.operatorModeStateService.realmURL;
  }

  @action
  private handleRemoveCard(cardId: string) {
    this.args.removeCard(cardId);
  }

  @action
  private handleChooseCard(cardId: string) {
    if (this.args.chooseCard) {
      this.args.chooseCard(cardId);
    }
  }

  @action
  private handleChooseFile(file: FileDef) {
    if (this.args.chooseFile) {
      this.args.chooseFile(file);
    }
  }

  @action
  private handleRemoveFile(file: FileDef) {
    this.args.removeFile(file);
  }

  private getUploadStatus = (file: FileDef): FileUploadStatus | undefined => {
    let sourceUrl = file.sourceUrl;
    if (!sourceUrl) {
      return undefined;
    }
    return this.args.fileUploadStates?.get(sourceUrl)?.status;
  };

  private getModalityWarning = (file: FileDef): string | undefined => {
    let modality = requiredModality(file.contentType);
    if (modality) {
      // Multimodal type — warn only if model doesn't support it
      let modalities = this.args.inputModalities;
      if (modalities && !modalities.includes(modality)) {
        return `Model does not support ${modalityLabel(modality)}. Only metadata will be sent.`;
      }
      return undefined;
    }
    // Non-multimodal, non-text files only get metadata sent
    if (!isTextBasedContentType(file.contentType)) {
      return 'File type not supported. Will send file metadata only.';
    }
    return undefined;
  };

  <template>
    <div class='attached-items' ...attributes>
      {{#if @isLoaded}}
        {{#each this.itemsToDisplay as |item|}}
          {{#if (isCardErrorJSONAPI item)}}
            {{#if item.id}}
              {{#if (this.isAutoAttachedCard item.id)}}
                <Tooltip @placement='top'>
                  <:trigger>
                    <CardPill
                      @cardId={{item.id}}
                      @borderType='dashed'
                      @onClick={{fn this.handleChooseCard item.id}}
                      @onRemove={{fn this.handleRemoveCard item.id}}
                      @urlForRealmLookup={{this.getCardErrorRealm item}}
                      data-test-autoattached-card={{item.id}}
                    />
                  </:trigger>

                  <:content>
                    {{#if @autoAttachedCardTooltipMessage}}
                      {{@autoAttachedCardTooltipMessage}}
                    {{else if (this.isAutoAttachedCard item.id)}}
                      Topmost card is shared automatically
                    {{/if}}
                  </:content>
                </Tooltip>
              {{else}}
                <CardPill
                  @cardId={{item.id}}
                  @borderType='solid'
                  @onRemove={{fn this.handleRemoveCard item.id}}
                  @urlForRealmLookup={{this.getCardErrorRealm item}}
                />
              {{/if}}
            {{/if}}
          {{else if (this.isCard item)}}
            {{#if (this.isAutoAttachedCard item.id)}}
              <Tooltip @placement='top'>
                <:trigger>
                  <CardPill
                    @cardId={{idFor item}}
                    @borderType='dashed'
                    @onClick={{fn this.handleChooseCard (idFor item)}}
                    @onRemove={{fn this.handleRemoveCard (idFor item)}}
                    @urlForRealmLookup={{urlForRealmLookup item}}
                    data-test-autoattached-card={{idFor item}}
                  />
                </:trigger>

                <:content>
                  {{#if @autoAttachedCardTooltipMessage}}
                    {{@autoAttachedCardTooltipMessage}}
                  {{else if (this.isAutoAttachedCard item.id)}}
                    Topmost card is shared automatically
                  {{/if}}
                </:content>
              </Tooltip>
            {{else}}
              <CardPill
                @cardId={{idFor item}}
                @borderType='solid'
                @onRemove={{fn this.handleRemoveCard (idFor item)}}
                @urlForRealmLookup={{urlForRealmLookup item}}
              />
            {{/if}}
          {{else}}
            {{#if (this.isAutoAttachedFile item)}}
              <Tooltip @placement='top'>
                <:trigger>
                  <FilePill
                    @file={{item}}
                    @borderType='dashed'
                    @onClick={{fn this.handleChooseFile item}}
                    @onRemove={{fn this.handleRemoveFile item}}
                    @uploadStatus={{this.getUploadStatus item}}
                    @onRetry={{if @retryFileUpload (fn @retryFileUpload item)}}
                    @warningMessage={{this.getModalityWarning item}}
                    data-test-autoattached-file={{item.sourceUrl}}
                  />
                </:trigger>
                <:content>
                  Currently opened file is shared automatically
                </:content>
              </Tooltip>
            {{else}}
              <FilePill
                @file={{item}}
                @borderType='solid'
                @onRemove={{fn this.handleRemoveFile item}}
                @uploadStatus={{this.getUploadStatus item}}
                @onRetry={{if @retryFileUpload (fn @retryFileUpload item)}}
                @warningMessage={{this.getModalityWarning item}}
              />
            {{/if}}
          {{/if}}
        {{/each}}
        {{#if
          (and
            (gt @items.length MAX_ITEMS_TO_DISPLAY)
            (not this.areAllItemsDisplayed)
          )
        }}
          <Pill
            @kind='button'
            {{on 'click' this.toggleViewAllAttachedCards}}
            data-test-view-all
          >
            View All ({{@items.length}})
          </Pill>
        {{/if}}
      {{/if}}
    </div>
    <style scoped>
      .attached-items {
        background-color: var(--boxel-light);
        color: var(--boxel-dark);
        display: flex;
        flex-wrap: wrap;
        gap: var(--boxel-sp-xxxs);
      }
    </style>
  </template>
}

function idFor(instance: CardDef) {
  return instance.id ?? instance[localId];
}
